import { Badge, BadgeStatus } from '../types/fingeringEditor';

/** アプリ内クリップボードの 1 バッジ分（重心相対座標 + 和音グループキー）。 */
export interface ClipboardItem {
  note: string;
  string: number | null;
  fret: number | null;
  dx: number;
  dy: number;
  /** コピー元の beatIndex（null 単音は id 由来の負値で一意化）。ペースト時の和音再グループ用。 */
  beatKey: number;
  noteIndex: number;
}

export interface ClipboardData {
  items: ClipboardItem[];
}

/** 選択バッジ群を重心相対のクリップボードデータへ変換する（純関数）。 */
export function buildClipboardItems(
  badges: Badge[],
  selectedIds: ReadonlySet<number>,
): ClipboardData {
  const picked = badges.filter((b) => selectedIds.has(b.id));
  if (picked.length === 0) return { items: [] };
  const cx = picked.reduce((s, b) => s + b.x, 0) / picked.length;
  const cy = picked.reduce((s, b) => s + b.y, 0) / picked.length;
  const items: ClipboardItem[] = picked.map((b) => ({
    note: b.note,
    string: b.string,
    fret: b.fret,
    dx: b.x - cx,
    dy: b.y - cy,
    // 実 beatIndex は >= 0。null 単音は衝突しない負値で個別グループ化。
    beatKey: b.beatIndex ?? -(b.id) - 1,
    noteIndex: b.noteIndex ?? 0,
  }));
  return { items };
}

/**
 * クリップボードデータを配置座標 (svgX, svgY) で新規 added バッジ群へ展開する（純関数）。
 * id は allocId()、和音グループ（同 beatKey）ごとに allocBeat() を 1 回だけ採番する。
 */
export function buildPastedBadges(
  data: ClipboardData,
  svgX: number,
  svgY: number,
  page: number,
  allocId: () => number,
  allocBeat: () => number,
): Badge[] {
  const beatKeyToNew = new Map<number, number>();
  return data.items.map((item) => {
    let nb = beatKeyToNew.get(item.beatKey);
    if (nb === undefined) {
      nb = allocBeat();
      beatKeyToNew.set(item.beatKey, nb);
    }
    const status: BadgeStatus = 'added';
    return {
      id: allocId(),
      x: svgX + item.dx,
      y: svgY + item.dy,
      note: item.note,
      string: item.string,
      fret: item.fret,
      status,
      beatIndex: nb,
      noteIndex: item.noteIndex,
      page,
    };
  });
}
